import cx from 'classnames';
import type { FC, PropsWithChildren, SyntheticEvent } from 'react';
import { Link } from 'react-router-dom';

import type { LocationDescriptor } from '@zen/types';
import { extractToAndState } from '@zen/utils/routing';

import Icon from '../Icon';

interface Props {
  className?: string;
  color?: 'default' | 'white';
  isExternal?: boolean;
  isStandalone?: boolean;
  linkTo?: LocationDescriptor;
  onClick?: (event: SyntheticEvent) => void;
  underline?: 'default' | 'always' | 'never';
}

const TextLink: FC<PropsWithChildren<Props>> = (props) => {
  const {
    children,
    isExternal = false,
    linkTo,
    onClick,
    isStandalone = true,
    className,
    color = 'default',
    underline = 'default'
  } = props;

  const textLinkWrapperClassName: string = cx(
    'text-sm group/text-wrap cursor-pointer',
    {
      'text-navy-base hover:text-azure-base': color === 'default',
      'text-white hover:text-white': color === 'white'
    },
    className
  );

  const textLinkClassName: string = cx('border-transparent border-solid border-b', {
    // Default behavior (maintains backward compatibility)
    'group-hover/text-wrap:border-azure-base': underline === 'default' && isStandalone,
    'border-navy-base group-hover/text-wrap:border-transparent': underline === 'default' && !isStandalone,
    // White variant default behavior: underlined by default, remove on hover
    'border-white group-hover/text-wrap:border-transparent': underline === 'default' && color === 'white',
    // Always show underline
    'border-navy-base group-hover/text-wrap:border-azure-base': underline === 'always' && color === 'default',
    'border-white group-hover/text-wrap:border-grey-lightest': underline === 'always' && color === 'white',
    // Never show underline
    'border-transparent': underline === 'never'
  });

  const TextLinkContent = (
    <span className={textLinkClassName} data-testid="text-link-content">
      {children}
    </span>
  );

  if (isExternal) {
    return (
      <a
        className={textLinkWrapperClassName}
        data-component="text-link"
        data-testid="text-link"
        href={linkTo as string}
        onClick={onClick}
        target="_blank"
        rel="noreferrer"
      >
        {TextLinkContent}
        <Icon className="ml-1 text-sm" icon="zicon-diagonal-arrow" />
      </a>
    );
  }

  const handleClick = (event: SyntheticEvent): void => {
    onClick?.(event);
  };

  if (linkTo) {
    return (
      <Link
        className={textLinkWrapperClassName}
        data-component="text-link"
        data-testid="text-link"
        onClick={handleClick}
        {...extractToAndState(linkTo)}
      >
        {TextLinkContent}
      </Link>
    );
  }

  return (
    <span className={textLinkWrapperClassName} data-component="text-link" data-testid="text-link" onClick={handleClick}>
      {TextLinkContent}
    </span>
  );
};

export type { Props as TextLinkProps };

export default TextLink;
